import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';
import {LoadMoreComponent} from "./load-more";

export class DDFilterController {
    static readonly HIDDEN_CLASS = 'd-none';
    // Element getters
    private readonly activeFiltersBodyContainer: HTMLDivElement = document.querySelector('#js-active-filters-body');
    private readonly activeFiltersModalContainer: HTMLDivElement = document.querySelector('#js-active-filters-modal');
    private readonly companiesAmountContainer: HTMLElement = document.querySelector('#js-companies-amount-container');
    private readonly filtersContainer: HTMLElement = document.querySelector('#js-filter-component');
    private readonly loadingSpinner: HTMLElement = document.querySelector('#company-list #loading-spinner');
    private readonly noResultsMessages: HTMLParagraphElement[] = Array.from(document.querySelectorAll('.js-no-results-message'));
    private readonly rankHintIcon: HTMLElement = document.querySelector('#js-rank-explanation-popover');
    private readonly resetFiltersElements: HTMLLinkElement[] = Array.from(document.querySelectorAll('.js-reset-filters-button'));
    private readonly sortByControlLabel: HTMLElement = document.querySelector('#js-sort-by-control-label');
    private readonly typesOfMovesControlLabel: HTMLElement = document.querySelector('#js-type-of-service-label');
    private readonly featuresControlLabel: HTMLElement = document.querySelector('#js-features-label');
    private readonly noResultsTag: HTMLElement = document.querySelector('.js-no-results');
    private readonly citiesListContainer: HTMLElement = document.querySelector('.js-cities-list');
    private readonly modalSubmitButton: HTMLButtonElement = document.querySelector('#js-modal-submit');

    private readonly companiesRequestDebounce = 500;
    private loadMoreComponent: LoadMoreComponent;

    constructor() {
        this.loadMoreComponent = new LoadMoreComponent();
    }

    public activate(): void {
        this.loadMoreComponent.init();
        this.resetFiltersEventListener();
        this.filterChangeEventListener();
        if (this.rankHintIcon) {
            this.rankHintIconEventListeners();
        }
        this.showSelectedFilters();
    }

    public activatePopovers(): void {
        const popovers = document.querySelectorAll('[data-toggle="popover"]');
        if (popovers.length > 0) {
            setTimeout(() => {
                jQuery('[data-toggle="popover"]').popover();
            }, 500);
        }
    }

    public debouncedCitySearch = debounce((input: HTMLInputElement): void => {
        this.citySearch(input);
    }, 200);

    private citySearch(input: HTMLInputElement): void {
        const filter = input.value.toUpperCase();
        const citiesList = this.citiesListContainer.querySelectorAll('.js-area-container');

        for (let i = 0; i < citiesList.length; i++) {
            if (citiesList[i].getElementsByTagName('a')[0].innerText.trim().toUpperCase().indexOf(filter) > -1) {
                (citiesList[i] as HTMLInputElement).classList.remove(DDFilterController.HIDDEN_CLASS);
            } else {
                (citiesList[i] as HTMLInputElement).classList.add(DDFilterController.HIDDEN_CLASS);
            }
        }

        const hiddenElementsCollection = this.citiesListContainer.querySelectorAll(
            `.js-area-container.${DDFilterController.HIDDEN_CLASS}`
        );

        if (hiddenElementsCollection.length === citiesList.length) {
            this.noResultsTag.classList.remove(DDFilterController.HIDDEN_CLASS);
        } else {
            this.noResultsTag.classList.add(DDFilterController.HIDDEN_CLASS);
        }
    }

    private showActiveFiltersContainers(): void {
        if (this.activeFiltersBodyContainer.classList.contains('d-none')) {
            this.activeFiltersBodyContainer.classList.remove('d-none');
        }

        if (this.activeFiltersModalContainer.classList.contains('d-none')) {
            this.activeFiltersModalContainer.classList.remove('d-none');
        }
    }

    private showNoResultsMessage(): void {
        this.noResultsMessages.map((noResultsMessage: HTMLElement) => {
            if (noResultsMessage.classList.contains('d-none')) {
                noResultsMessage.classList.remove('d-none');
            }
        });
    }

    private hideActiveFiltersContainers(): void {
        if (!this.activeFiltersBodyContainer.classList.contains('d-none')) {
            this.activeFiltersBodyContainer.classList.add('d-none');
        }

        if (!this.activeFiltersModalContainer.classList.contains('d-none')) {
            this.activeFiltersModalContainer.classList.add('d-none');
        }
    }

    private hideNoResultsMessage(): void {
        this.noResultsMessages.map((noResultsMessage: HTMLElement) => {
            if (!noResultsMessage.classList.contains('d-none')) {
                noResultsMessage.classList.add('d-none');
            }
        });
    }

    private resetFiltersEventListener(): void {
        this.resetFiltersElements.map((resetFiltersElement: HTMLElement) => {
            resetFiltersElement.addEventListener('click', (event: MouseEvent) => {
                event.preventDefault();
                this.resetFilters();
            });
        });
    }

    private resetFilters(): void {
        const inputs: HTMLInputElement[] = Array.from(this.filtersContainer.querySelectorAll('input'));
        inputs.filter((input: HTMLInputElement) => input.name !== 'sort_by' && input.checked)
            .forEach((input: HTMLInputElement) => {
                input.checked = false;
                this.updateQueryParams(input);
            });

        inputs[0].dispatchEvent(new Event('change'));

        this.hideActiveFiltersContainers();
    }

    private getCompanies(): void {
        const url = new URL(window.location.href);
        this.loadMoreComponent.updateCompaniesList(url.search)
            .then(response => {
                this.hideLoadingState();

                if (response?.ajaxPagination?.totalAmount > 0) {
                    this.showCompaniesAmountContainer();
                }

                if (response?.emptyFilterResults) {
                    this.showNoResultsMessage();
                    this.modalSubmitButton.disabled = true;
                    this.updateMobileCompaniesAmount(0);
                    this.updateDesktopCompaniesAmount(response?.ajaxPagination?.totalAmount);
                } else {
                    this.updateAmountLabel(response.ajaxPagination.totalAmount);
                }

                return response;
            })
            .catch((error) => {
                this.hideLoadingState();
                console.error('Error:', error);
            });
    }

    private showLoadingState(): void {
        this.loadingSpinner.classList.remove('d-none');
        this.modalSubmitButton.classList.add('btn-loading');
        this.modalSubmitButton.disabled = true;
    }

    private hideLoadingState(): void {
        this.loadingSpinner.classList.add('d-none');
        this.modalSubmitButton.classList.remove('btn-loading');
        this.modalSubmitButton.disabled = false;
    }

    private filterChangeEventListener(): void {
        let debouncedGetCompanies;
        this.filtersContainer.querySelectorAll('input')
            .forEach((input: HTMLInputElement, index: number, elements: NodeListOf<HTMLInputElement>) => {
                input.addEventListener('change', (event: Event) => {
                    if (debouncedGetCompanies) {
                        debouncedGetCompanies.cancel();
                    }
                    const target = event.target as HTMLInputElement;
                    this.syncTwinElement(target);
                    this.updateQueryParams(target);
                    switch (target.name) {
                        case 'sort_by':
                            this.updateSortByControls(target, elements);
                            break;
                        case 'type_of_service':
                        case 'features':
                            this.updateActiveFilters();
                            break;
                        default:
                            throw new Error('Unknown filter type');
                    }
                    debouncedGetCompanies = debounce(this.getCompanies.bind(this), this.companiesRequestDebounce);
                    debouncedGetCompanies();
                    this.hideCompaniesAmountContainer();
                    this.hideNoResultsMessage();
                    this.showLoadingState();
                });
            }
        );
    }

    private updateSortByControls(target: HTMLInputElement, elements: NodeListOf<HTMLInputElement>): void {
        this.updateSortByControlLabel(target);

        // we do not need to hide/show hint icon if there are no rank sorting option
        if (!Array.from(elements).some(e => e.value === 'rank')) {
            return;
        }

        if (target.value === 'rank') {
            this.showRankHintIcon();
        } else {
            this.hideRankHintIcon();
        }
    }

    private syncTwinElement(target: HTMLInputElement): void {
        const twinTarget = document.querySelector(`#${target.dataset.twinId}`) as HTMLInputElement;
        if (twinTarget) {
            twinTarget.checked = target.checked;
        }
    }

    private updateSortByControlLabel(target: HTMLInputElement): void {
        const textNodes = Array.from(target.labels[0].childNodes)
            .filter(node => node.nodeType === Node.TEXT_NODE);
        this.sortByControlLabel.textContent = textNodes[0].textContent;
    }

    private showRankHintIcon(): void {
        this.rankHintIcon.classList.remove('d-none');
        this.sortByControlLabel.classList.remove('sd-mr-1_5');
    }

    private hideRankHintIcon(): void {
        this.rankHintIcon.classList.add('d-none');
        this.sortByControlLabel.classList.add('sd-mr-1_5');
    }

    private updateActiveFilters(): void {
        this.updateActiveFiltersControls();
        this.updateActiveFiltersInputs();
    }

    private updateActiveFiltersInputs(): void {
        Array.from(this.activeFiltersBodyContainer.querySelectorAll('.badge')).forEach(badge => {
            badge.remove();
        });

        Array.from(this.filtersContainer.querySelectorAll('.dynamic-directory-filter input'))
            .filter((input: HTMLInputElement) => ['type_of_service', 'features'].includes(input.name) && input.checked)
            .forEach((input: HTMLInputElement) => {
                this.addBadge(input);
            });

        if (this.activeFiltersBodyContainer.querySelectorAll('.badge').length > 0) {
            this.showActiveFiltersContainers();
        } else {
            this.hideActiveFiltersContainers();
        }
    }

    private addBadge(input: HTMLInputElement): void {
        const badgeId: string = uniqueId('badge-');
        const badgeTemplate = document.querySelector("#js-filter-badge") as HTMLTemplateElement;
        const badgeElement = badgeTemplate.content.cloneNode(true) as HTMLButtonElement;
        const badgeButton = badgeElement.querySelector('button');
        badgeButton.id = badgeId;
        badgeButton.dataset.referencedInputId = input.id;
        badgeButton.querySelector('.js-badge-label').textContent = input.labels[0].textContent;
        this.activeFiltersBodyContainer.prepend(badgeElement);
        this.attachEventToBadge(badgeId);
    }

    private attachEventToBadge(badgeId: string): void {
        document.querySelector(`#${badgeId}`).addEventListener('click', () => {
            const badge: HTMLButtonElement = document.querySelector(`#${badgeId}`) as HTMLButtonElement;
            const referencedInput: HTMLInputElement = document.querySelector(`#${badge.dataset.referencedInputId}`) as HTMLInputElement;
            if (referencedInput.type === 'radio') {
                referencedInput.checked = false;
                referencedInput.dispatchEvent(new Event('change'));
            } else {
                referencedInput.click();
            }
            badge.remove();
        }, {once: true});
    }

    private updateQueryParams(target: HTMLInputElement): void {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);

        switch (target.name) {
            case 'sort_by':
                this.updateSortParam(params, target);
                break;
            case 'type_of_service':
                this.updateTypeOfServiceParam(params, target);
                break;
            case 'features':
                this.updateFeaturesParam(params);
                break;
            default:
                throw new Error('Unknown filter type');
        }

        url.search = params.toString();
        window.history.replaceState({}, '', url.toString());
    }

    private updateSortParam(params: URLSearchParams, target: HTMLInputElement): void {
        let splitName = target.name.split('_');
        splitName[1] = splitName[1].charAt(0).toUpperCase() + splitName[1].slice(1);
        const sortingParamName = splitName.join('');
        if (target.checked) {
            params.set(sortingParamName, target.value);
        } else {
            params.delete(sortingParamName);
        }
    }

    private updateTypeOfServiceParam(params: URLSearchParams, target: HTMLInputElement): void {
        if (target.checked) {
            params.set('filter[type]', target.value);
        } else {
            params.delete('filter[type]');
        }
    }

    private updateFeaturesParam(params: URLSearchParams): void {
        const availableFeatureFilters = JSON.parse(this.filtersContainer.dataset.featuresArray);
        availableFeatureFilters.map((feature: string, index: number) => {
            const input = this.filtersContainer.querySelector(`input[value="${feature}"]`) as HTMLInputElement;
            if (input.checked) {
                params.set(`filter[features][${index}]`, feature);
            } else {
                params.delete(`filter[features][${index}]`);
            }
        });
    }

    private showSelectedFilters(): void {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        params.forEach((value) => {
            const input = this.filtersContainer.querySelector(`input[value="${value}"]`) as HTMLInputElement;
            if (input) {
                input.checked = true;
                this.syncTwinElement(input);
                if (input.type === 'radio' && input.name === 'sort_by') {
                    this.updateSortByControls(input, this.filtersContainer.querySelectorAll('input'));
                } else {
                    this.updateActiveFilters();
                }
            }
        });
    }

    private hideCompaniesAmountContainer(): void {
        this.companiesAmountContainer.classList.add('d-none');
    }

    private updateAmountLabel(totalAmount: number): void {
        this.updateDesktopCompaniesAmount(totalAmount);
        this.updateMobileCompaniesAmount(totalAmount);
    }

    private updateMobileCompaniesAmount(totalAmount: number): void {
        const amountLabelModal = this.modalSubmitButton.querySelector('#js-companies-amount-modal');
        if (amountLabelModal) {
            amountLabelModal.textContent = totalAmount.toString();
        }
    }

    private updateDesktopCompaniesAmount(totalAmount: number): void {
        const amountLabelDesktop = this.companiesAmountContainer.querySelector('#js-companies-amount');
        if (amountLabelDesktop) {
            amountLabelDesktop.textContent = totalAmount.toString()
        }
    }

    private showCompaniesAmountContainer(): void {
        this.companiesAmountContainer.classList.remove('d-none');
    }

    private rankHintIconEventListeners(): void {
        this.rankHintIcon.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
        });

        // we need to close popover when user clicks outside of it
        document.body.addEventListener('click', () => {
            if(this.rankHintIcon.hasAttribute('aria-describedby')) {
                $(this.rankHintIcon).popover('hide');
            }
        });
    }

    private updateActiveFiltersControls(): void {
        this.toggleTypeOfServiceActiveFilterControl();
        this.toggleFeatureActiveFilterControl();
    }

    private toggleFeatureActiveFilterControl(): void {
        const featuresActiveFilters = Array.from(
            this.filtersContainer.querySelectorAll('.dynamic-directory-filter input[name="features"]:checked')
        );
        if (featuresActiveFilters.length > 0) {
            this.setFeaturesControlActive(featuresActiveFilters.length);
        } else {
            this.setFeaturesControlInactive();
        }
    }

    private toggleTypeOfServiceActiveFilterControl(): void {
        const typeOfServiceActiveFilter = Array.from(
            this.filtersContainer.querySelectorAll('.dynamic-directory-filter input[name="type_of_service"]:checked')
        );
        if (typeOfServiceActiveFilter.length > 0) {
            this.setTypesOfMovesControlActive();
        } else {
            this.setTypesOfMovesControlInactive();
        }
    }

    private setTypesOfMovesControlActive(): void {
        this.typesOfMovesControlLabel.classList.add('font-weight-bold');
    }

    private setTypesOfMovesControlInactive(): void {
        this.typesOfMovesControlLabel.classList.remove('font-weight-bold');
    }

    private setFeaturesControlActive(amountOfActiveFilters: number): void {
        this.featuresControlLabel.classList.add('font-weight-bold');
        const counterContainer = this.featuresControlLabel.querySelector('#features-count-active-filters');
        counterContainer.textContent = ` (${amountOfActiveFilters})`;
        counterContainer.classList.remove('d-none');
    }

    private setFeaturesControlInactive(): void {
        this.featuresControlLabel.classList.remove('font-weight-bold');
        this.featuresControlLabel.querySelector('#features-count-active-filters').classList.add('d-none');
    }
}

(<any>window).skyComponents = (<any>window).skyComponents || {};
(<any>window).skyComponents.ddFilterController = new DDFilterController();
(<any>window).skyComponents.ddFilterController.activate();
(<any>window).skyComponents.ddFilterController.activatePopovers();
